import React, { Component, ErrorInfo, ReactNode } from 'react';
import { trackError, ErrorSeverity, type ErrorContext } from '../../utils/error-tracking';
import { createNotification } from '../../utils/dom';

interface SilentErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  logToConsole?: boolean;
  logToClickHouse?: boolean;
  showNotification?: boolean; // Show user notification
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  componentName?: string;
  suppressTypes?: string[];
}

interface SilentErrorBoundaryState {
  hasError: boolean;
  errorCount: number;
}

export class SilentErrorBoundary extends Component<SilentErrorBoundaryProps, SilentErrorBoundaryState> {
  constructor(props: SilentErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(_error: Error): Partial<SilentErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const {
      logToConsole = process.env.NODE_ENV === 'development',
      logToClickHouse = true,
      showNotification = true,
      onError,
      componentName = 'Unknown',
      suppressTypes = [],
    } = this.props;

    // Check if this error type should be suppressed
    const shouldSuppress = suppressTypes.some(type =>
      error.name === type || error.message.includes(type)
    );

    if (shouldSuppress) {
      // Log suppressed error to ClickHouse for visibility, but skip notification/UI disruption
      if (logToClickHouse) {
        trackError(error, ErrorSeverity.WARNING, {
          component: componentName,
          boundary: 'silent',
          tags: {
            errorBoundary: 'silent',
            componentName,
            suppressed: 'true',
          },
        });
      }
      setTimeout(() => this.setState({ hasError: false }), 0);
      return;
    }

    // Update error count
    this.setState(prevState => ({
      errorCount: prevState.errorCount + 1,
    }));

    // Show notification to user
    if (showNotification) {
      createNotification({
        type: 'error',
        msg: 'Something went wrong. Please try again.',
        dismissed: false,
      });
    }

    // Log to console if enabled
    if (logToConsole) {
      trackError(`Silent Error in ${componentName}`, ErrorSeverity.WARNING, {
        component: componentName,
        boundary: 'silent',
        tags: { errorCount: (this.state.errorCount + 1).toString() },
      });
      trackError(error, ErrorSeverity.WARNING, {
        component: componentName,
        boundary: 'silent',
        componentStack: errorInfo.componentStack,
      });
    }

    // Send to ClickHouse if enabled
    if (logToClickHouse) {
      const context: ErrorContext = {
        component: componentName,
        boundary: 'silent',
        componentStack: errorInfo.componentStack,
        tags: {
          errorBoundary: 'silent',
          componentName,
          errorCount: (this.state.errorCount + 1).toString(),
        },
      };

      trackError(error, ErrorSeverity.WARNING, context);
    }

    // Call custom error handler
    onError?.(error, errorInfo);

    // Reset error state immediately - don't break the UI
    // This allows the component to continue rendering
    setTimeout(() => {
      this.setState({ hasError: false });
    }, 0);
  }

  render() {
    const { hasError } = this.state;
    const { children, fallback } = this.props;

    // If there's an error, show fallback or render nothing
    // Re-rendering failing children would cause React to escalate the error
    if (hasError) {
      return fallback || null;
    }

    return children;
  }
}

// Hook to wrap components that might fail but shouldn't break the app
export const withSilentErrorBoundary = <P extends object>(
  WrappedComp: React.ComponentType<P>,
  componentName?: string,
  options?: Omit<SilentErrorBoundaryProps, 'children'>
) => {
  const WrappedComponent = React.forwardRef<unknown, P>((props, ref) => {
    return (
      <SilentErrorBoundary componentName={componentName || WrappedComp.displayName || WrappedComp.name} {...options}>
        {React.createElement(WrappedComp, { ...props, ref } as unknown as P)}
      </SilentErrorBoundary>
    );
  });
  return WrappedComponent;
};

// Wrapper component for non-critical features
export const NonCriticalBoundary: React.FC<{
  children: ReactNode;
  name?: string;
  fallback?: ReactNode;
}> = ({ children, name = 'NonCritical', fallback }) => {
  return (
    <SilentErrorBoundary
      componentName={name}
      fallback={fallback}
      logToConsole={false}
      logToClickHouse={true}
    >
      {children}
    </SilentErrorBoundary>
  );
};

// Wrapper for third-party components that might throw errors
export const ThirdPartyBoundary: React.FC<{
  children: ReactNode;
  name?: string;
  fallback?: ReactNode;
  suppressTypes?: string[];
}> = ({ children, name = 'ThirdParty', fallback, suppressTypes = [] }) => {
  return (
    <SilentErrorBoundary
      componentName={name}
      fallback={fallback}
      suppressTypes={[
        'ChunkLoadError',
        'Network request failed',
        'ResizeObserver loop limit exceeded',
        ...suppressTypes,
      ]}
      logToClickHouse={true}
    >
      {children}
    </SilentErrorBoundary>
  );
};

// Wrapper for analytics and tracking code
export const AnalyticsBoundary: React.FC<{
  children: ReactNode;
  name?: string;
}> = ({ children, name = 'Analytics' }) => {
  return (
    <SilentErrorBoundary
      componentName={name}
      logToConsole={false}
      logToClickHouse={true}
    >
      {children}
    </SilentErrorBoundary>
  );
};

export default SilentErrorBoundary;